/**
 * Scout › Candidato — detalle completo de un candidato: scoring por dimensión
 * con la evidencia citada del transcript, resumen, datos de contacto, CV y link
 * a la sesión de voz (donde se ve el transcript). Ver docs/scout-plan/04.
 *
 * La evidencia (`scores[dim].evidence`) son frases textuales del candidato que
 * el distiller `scout-scoring` cita para justificar cada score — el "por qué"
 * de la recomendación. Esta página existe para no perder ese detalle.
 */
import { type ReactElement, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchScoutCandidate,
  type ScoutCandidate,
  type ScoutResult,
} from '../../api/admin/scout.js';
import { dimLabel, fmt, recLabel, recPill, statusPill } from './format.js';

const SCORE_MAX = 5;

const cardStyle: CSSProperties = { padding: 16, marginTop: 16 };
const labelStyle: CSSProperties = {
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-tertiary)',
};

/** Barra de 1–5 para visualizar un score de dimensión. */
function ScoreBar({ score }: { score: number }): ReactElement {
  const pct = Math.max(0, Math.min(1, score / SCORE_MAX)) * 100;
  const tone = score >= 4 ? 'var(--ok, #6bbf6b)' : score <= 2 ? 'var(--error, #d36b6b)' : 'var(--text-secondary)';
  return (
    <div
      aria-label={`${score} de ${SCORE_MAX}`}
      style={{
        position: 'relative',
        height: 6,
        borderRadius: 3,
        background: 'var(--surface-3)',
        overflow: 'hidden',
        minWidth: 80,
      }}
    >
      <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: tone }} />
    </div>
  );
}

function DimensionRow({ dimKey, dim }: { dimKey: string; dim: ScoutResult['scores'][string] }): ReactElement {
  return (
    <div
      style={{
        display: 'grid',
        gap: 8,
        padding: '14px 0',
        borderTop: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ minWidth: 140 }}>{dimLabel(dimKey)}</strong>
        <ScoreBar score={dim.score} />
        <span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {dim.score} / {SCORE_MAX}
        </span>
      </div>
      {dim.evidence ? (
        <blockquote
          style={{
            margin: 0,
            padding: '8px 12px',
            borderLeft: '3px solid var(--border-default)',
            background: 'var(--surface-2)',
            borderRadius: 4,
            color: 'var(--text-secondary)',
            fontSize: 13.5,
            lineHeight: 1.5,
          }}
        >
          {dim.evidence}
        </blockquote>
      ) : (
        <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>
          Sin evidencia citada para esta dimensión.
        </span>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={labelStyle}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

export function ScoutCandidateDetailPage(): ReactElement {
  const { candidateId } = useParams<{ candidateId: string }>();
  const query = useQuery({
    queryKey: ['admin', 'scout', 'candidate', candidateId],
    queryFn: () => fetchScoutCandidate(candidateId!),
    enabled: !!candidateId,
  });

  if (query.isPending) return <div className="loading">Cargando…</div>;
  if (query.isError) return <div className="loading">Error: {query.error.message}</div>;

  const { search, roleTitle, candidate } = query.data;
  const result = candidate.result;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Scout · {candidate.fullName}</h1>
          <p className="page-sub">
            <Link to={`/scout/${search.id}`}>← Volver a “{search.title}”</Link>
            {' · '}
            <span style={{ color: 'var(--text-tertiary)' }}>{roleTitle}</span>
          </p>
        </div>
      </div>

      {/* Recomendación + resumen */}
      <section className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span className={statusPill(candidate.status)}>{candidate.status}</span>
          {result ? (
            <>
              <span className={recPill(result.recommendation)} style={{ fontSize: 14 }}>
                {recLabel(result.recommendation)}
              </span>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>
                Confianza: {result.confidence === 'high' ? 'alta' : 'baja'} · evaluado{' '}
                {fmt(result.createdAt)}
              </span>
            </>
          ) : (
            <span style={{ color: 'var(--text-tertiary)' }}>
              Todavía no evaluado — el scoring corre en segundo plano al cerrar la entrevista.
            </span>
          )}
        </div>
        {result?.summary && (
          <p style={{ marginBottom: 0, marginTop: 14, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
            {result.summary}
          </p>
        )}
      </section>

      {/* Desglose por dimensión con evidencia citada */}
      {result && Object.keys(result.scores).length > 0 && (
        <section className="card" style={cardStyle}>
          <h2 style={{ margin: '0 0 4px', fontSize: 16 }}>Evaluación por dimensión</h2>
          <p style={{ ...labelStyle, marginTop: 0 }}>
            Score 1–5 + cita textual del candidato que lo justifica
          </p>
          {Object.entries(result.scores).map(([key, dim]) => (
            <DimensionRow key={key} dimKey={key} dim={dim} />
          ))}
        </section>
      )}

      {/* Datos de contacto + sesión */}
      <section className="card" style={cardStyle}>
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>Candidato</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          <Field label="Email">
            <a href={`mailto:${candidate.email}`}>{candidate.email}</a>
          </Field>
          <Field label="Teléfono">{candidate.phone ?? '—'}</Field>
          <Field label="Consentimiento">{fmt(candidate.consentAt)}</Field>
          <Field label="Registrado">{fmt(candidate.createdAt)}</Field>
          <Field label="CV">
            {candidate.hasCv ? (
              <a href={`/api/admin/scout/candidates/${candidate.id}/cv`}>Descargar PDF</a>
            ) : (
              '—'
            )}
          </Field>
          <Field label="Sesión de voz">
            <SessionLink voiceSessionId={candidate.voiceSessionId} />
          </Field>
        </div>
      </section>
    </>
  );
}

function SessionLink({ voiceSessionId }: { voiceSessionId: ScoutCandidate['voiceSessionId'] }): ReactElement {
  if (!voiceSessionId) {
    return <span style={{ color: 'var(--text-tertiary)' }}>sin sesión vinculada</span>;
  }
  return <Link to={`/sessions/${voiceSessionId}`}>Ver sesión y transcript →</Link>;
}
